/* eslint-disable no-bitwise */
import { parseAdtsFrame } from '../adts/adts';
import type { CaptionDataTriplet } from '../closed-captions/cdp/types';
import {
  access_unit_delimiter_rbsp, slice_layer_without_partitioning_rbsp_idr,
  seq_parameter_set_rbsp, decodeRBSP, parseSequenceparameterSet,
  getCodecString,
  sei_rbsp,
  slice_layer_without_partitioning_rbsp_non_idr,
  inspectSliceHeader,
  type H264FrameInfo,
} from '../h264/h264';
import { parseSeiForClosedCaptions } from '../h264/seiClosedCaptions';
import type { AudioCodecSpecificData } from '../types/audioCodecSpecificData';
import { invariant } from '../util/invariant';
import type { EncodedAudioData, EncodedAudioFrame, EncodedData, EncodedDataWithCaptions, EncodedVideoData, EncodedVideoFrame } from './demuxerTypes';

const MPEGTS_PACKET_LENGTH = 188;
const MPEGTS_SYNC = 0x47;

const H264_STREAM_TYPE = 0x1B;
const ADTS_STREAM_TYPE = 0x0F;


// We need to prefix each NAL unit with the start code 0x000001
// in order to produce an Annex B byte stream as expected by default
// by the VideoDecoder
const NAL_PREFIX = new Uint8Array([0, 0, 1]);

interface ParsedMpegTsPacket {
  pid: number;
  payloadUnitStartIndicator: boolean;
  data: Uint8Array;
}


interface PmtParseState {
  buffer: Uint8Array;
  sectionLength: number;
  tableEnd: number;
  programInfoLength: number;
}


interface ProgramMapTable {
  videoPid: number | undefined;
  audioPids: number[];
}


interface PES {
  pts: number;
  dts: number;
  data: Uint8Array;
  packetLength: number;
  dataAlignmentIndicator: boolean;
}


interface NAL {
  pts: number;
  dts: number;
  nalUnitCode: number;
  data: Uint8Array;
}


interface PacketBuffer {
  pid: number;
  video: boolean;
  data: Uint8Array[];
  size: number;
  pesBuffer: PES[];
}


/**
 * Given a byte array containing a ts segment, returns
 * the decoded data needed to feed web codec decoders.
 *
 * If trimAudioFramesBeforeVideo is true, we will discard the
 * audio frames with a DTS value lower than the lowest video
 * frame DTS. This is meant to replicate mux.js behaviour
 * for the very first segment of a media. This value should
 * therefore be true only when demuxing the first media segment.
 */
export function demuxTsSegment(
  tsSegmentBytes: Uint8Array,
  trimAudioFramesBeforeVideo: boolean,
): EncodedData {
  return new MpegTsDemuxer(tsSegmentBytes, trimAudioFramesBeforeVideo, undefined).parse();
}


/**
 * Closed caption data triplets need to be processed in PTS order.
 * Since the triplets are emitted by the demuxer for each frame in DTS order,
 * we need to store them first in a structure that knows about the PTS so that
 * we can sort them before returning them.
 */
interface FrameCaptionDataWithPts {
  pts: number;
  captionDataTriplets: CaptionDataTriplet[];
}


export function demuxTsSegmentWithCaptions(
  tsSegmentBytes: Uint8Array,
  trimAudioFramesBeforeVideo: boolean,
): EncodedDataWithCaptions {

  const framesData: FrameCaptionDataWithPts[] = [];
  const processClosedCaptionData = (pts: number, captionDataTriplets: CaptionDataTriplet[]) => {
    framesData.push({ pts, captionDataTriplets });
  };

  const encodedData = new MpegTsDemuxer(tsSegmentBytes, trimAudioFramesBeforeVideo, processClosedCaptionData).parse();
  framesData.sort((a, b) => a.pts - b.pts);

  return {
    encodedVideoData: encodedData.encodedVideoData,
    encodedAudioData: encodedData.encodedAudioData,
    frameCaptionData: framesData.map(frameData => frameData.captionDataTriplets),
  };
}



class MpegTsDemuxer {

  private earliestVideoDts: number | undefined;
  private packets: ParsedMpegTsPacket[] = [];

  constructor(
    private tsSegmentBytes: Uint8Array,
    private trimAudioFramesBeforeVideo: boolean,
    /**
     * If defined, this callback will be invoked once for each frame with all the closed caption
     * data packets that have been accumulated since last frame.
     * If not defined, the closed cpation packets will be ignored.
     */
    private processClosedCaptionData: ((pts: number, captionDataTriplets: CaptionDataTriplet[]) => void) | undefined,
  ) {}

  private readMpegTsPackets(): void {
    invariant((this.tsSegmentBytes.length % MPEGTS_PACKET_LENGTH) === 0, () => `Segment size ${this.tsSegmentBytes.length} is not divisible by ${MPEGTS_PACKET_LENGTH}`);

    const nPackets = this.tsSegmentBytes.length / MPEGTS_PACKET_LENGTH;
    for (let i = 0; i < nPackets; i++) {
      const start = i * MPEGTS_PACKET_LENGTH;
      const packet = this.tsSegmentBytes.subarray(start, start + MPEGTS_PACKET_LENGTH);
      invariant(packet[0] === MPEGTS_SYNC, () => `Packet ${i} does not start with ${MPEGTS_SYNC}`);


      const payloadUnitStartIndicator = !!(packet[1]! & 0x40);
      let pid = packet[1]! & 0x1f;
      pid <<= 8;
      pid |= packet[2]!;
      let offset = 4;

      if (((packet[3]! & 0x30) >>> 4) > 0x01) {
        offset += packet[offset]! + 1;
      }
      this.packets.push({
        pid,
        payloadUnitStartIndicator,
        data: packet.subarray(offset),
      });
    }
  }


  private getPsiAjustedOffset(p: ParsedMpegTsPacket) {
    let offset = 0;
    if (p.payloadUnitStartIndicator) {
      offset += p.data[offset]! + 1;
    }
    return offset;
  }


  private findPmtId(): number | null {
    for (const p of this.packets) {
      if (p.pid === 0) {
        // The PAT packet has the PID 0
        const offset = this.getPsiAjustedOffset(p);
        const pmtPid = (p.data[offset + 10]! & 0x1F) << 8 | p.data[offset + 11]!;
        return pmtPid;
      }
    }
    return null;
  }


  private parsePmtPacket(pmtParseState: PmtParseState, p: ParsedMpegTsPacket): ProgramMapTable | undefined {
    let offset = this.getPsiAjustedOffset(p);
    const payload = p.data.subarray(offset);

    pmtParseState.sectionLength = (payload[1]! & 0x0f) << 8 | payload[2]!;
    pmtParseState.tableEnd = 3 + pmtParseState.sectionLength - 4;
    pmtParseState.programInfoLength = (payload[10]! & 0x0f) << 8 | payload[11]!;
    const newPmtBuffer = new Uint8Array(pmtParseState.buffer.length + payload.length);
    newPmtBuffer.set(pmtParseState.buffer, 0);
    newPmtBuffer.set(payload, pmtParseState.buffer.length);
    pmtParseState.buffer = newPmtBuffer;

    if (pmtParseState.buffer.length < pmtParseState.sectionLength) {
      // We don't have all the data yet
      return undefined;
    }

    let videoPid: number | undefined;
    const audioPids: number[] = [];

    offset = 12 + pmtParseState.programInfoLength;
    while (offset < pmtParseState.tableEnd) {
      const streamType = pmtParseState.buffer[offset]!;
      const pid = (pmtParseState.buffer[offset + 1]! & 0x1F) << 8 | pmtParseState.buffer[offset + 2]!;

      if (streamType === H264_STREAM_TYPE) {
        videoPid = pid;
      } else if (streamType === ADTS_STREAM_TYPE) {
        audioPids.push(pid);
      } else {
        console.log(`Unknown PID ${pid} with type ${streamType}`);
      }

      offset += ((pmtParseState.buffer[offset + 3]! & 0x0F) << 8 | pmtParseState.buffer[offset + 4]!) + 5;
    }

    return {
      videoPid,
      audioPids,
    };
  }


  private readProgramMapTable(pmtId: number): ProgramMapTable | undefined {
    const pmtParseState: PmtParseState = {
      buffer: new Uint8Array(0),
      sectionLength: 0,
      tableEnd: 0,
      programInfoLength: 0
    };
    for (const p of this.packets) {
      if (p.pid === pmtId) {
        const pmt = this.parsePmtPacket(pmtParseState, p);
        if (pmt) {
          return pmt;
        }
      }
    }
    return undefined;
  }


  private parsePes(payload: Uint8Array): PES | undefined {
    const startPrefix = payload[0]! << 16 | payload[1]! << 8 | payload[2]!;

    if (startPrefix !== 1) {
      // Since a fully formed PES packet must start with 0x000001, not
      // finding this suggests that the ts segment starts with the end of
      // a PES packet from the previous ts segment, in which case we ignore
      // the packet
      return undefined;
    }

    const packetLength = 6 + ((payload[4]! << 8) | payload[5]!);
    const dataAlignmentIndicator = (payload[6]! & 0x04) !== 0;


    let pts: number | undefined;
    let dts: number | undefined;

    const ptsDtsFlags = payload[7]!;

    // If we have a pts
    if (ptsDtsFlags & 0xC0) {
      pts = (payload[9]! & 0x0E) << 27 |
        (payload[10]! & 0xFF) << 20 |
        (payload[11]! & 0xFE) << 12 |
        (payload[12]! & 0xFF) <<  5 |
        (payload[13]! & 0xFE) >>>  3;
      pts *= 4;
      pts += (payload[13]! & 0x06) >>> 1;
      dts = pts;

      // If we have an explicit dts
      if (ptsDtsFlags & 0x40) {
        dts = (payload[14]! & 0x0E) << 27 |
          (payload[15]! & 0xFF) << 20 |
          (payload[16]! & 0xFE) << 12 |
          (payload[17]! & 0xFF) << 5 |
          (payload[18]! & 0xFE) >>> 3;
        dts *= 4;
        dts += (payload[18]! & 0x06) >>> 1;
      }
    }

    const data = payload.subarray(9 + payload[8]!);

    if (pts === undefined || dts === undefined) {
      // We cannot work with a packet without these infos
      return undefined;
    }

    return {
      pts,
      dts,
      packetLength,
      dataAlignmentIndicator,
      data,
    };
  }


  /**
   * Given PS packets that belong to a given media stream,
   * re-assemble the PES  packet.
   */
  private processBufferedPackets(b: PacketBuffer) {
    if (b.size < 9) {
      return;
    }

    const packetData = new Uint8Array(b.size);
    let offset = 0;
    for (const data of b.data) {
      packetData.set(data, offset);
      offset += data.byteLength;
    }

    const pes = this.parsePes(packetData);

    const canFlush = (b.video || pes === undefined || pes.packetLength <= b.size);
    if (canFlush) {
      b.data = [];
      b.size = 0;
      if (pes) {
        b.pesBuffer.push(pes);
      }
    }
  }


  /**
   * Iterate over all the Program Stream packets to
   * reassemble the Packetized Elementary Streams for video
   * and audio
   */
  private readPacketizedElementaryStreams(pmt: ProgramMapTable): Record<number, PES[]> {
    const packetBuffers: Record<number, PacketBuffer> = {};
    if (pmt.videoPid !== undefined) {
      packetBuffers[pmt.videoPid] = {
        pid: pmt.videoPid,
        video: true,
        data: [],
        size: 0,
        pesBuffer: [],
      };
    }
    for (const pid of pmt.audioPids) {
      packetBuffers[pid] = {
        pid,
        video: false,
        data: [],
        size: 0,
        pesBuffer: [],
      };
    }

    for (const p of this.packets) {
      const packetBuffer = packetBuffers[p.pid];
      if (!packetBuffer) {
        continue;
      }

      if (p.payloadUnitStartIndicator) {
        // A new packet is starting, we can process the packets we already have
        // for this stream
        this.processBufferedPackets(packetBuffer);
      }
      packetBuffers[p.pid]!.data.push(p.data);
      packetBuffers[p.pid]!.size += p.data.byteLength;
    }

    // Let's process all remaining packets
    for (const b of Object.values(packetBuffers)) {
      this.processBufferedPackets(b);
    }

    const results: Record<number, PES[]> = {};
    for (const b of Object.values(packetBuffers)) {
      results[b.pid] = b.pesBuffer;
    }

    return results;
  }


  private makeEncodedVideoFrame(nalus: NAL[]): EncodedVideoFrame {
    const pts = nalus[0]!.pts;
    const dts = nalus[0]!.dts;

    if (this.earliestVideoDts === undefined || dts < this.earliestVideoDts) {
      this.earliestVideoDts = dts;
    }

    let keyframe = false;
    let size = 0;

    for (const nalu of nalus) {
      size += nalu.data.byteLength + 3;

      const nalUnitTypeCode = nalu.data[0]! & 0x1f;
      if (nalUnitTypeCode === slice_layer_without_partitioning_rbsp_idr) {
        keyframe = true;
      }
    }

    const data = new Uint8Array(size);
    let offset = 0;
    for (const nalu of nalus) {
      data.set(NAL_PREFIX, offset);
      offset += 3;
      data.set(nalu.data, offset);
      offset += nalu.data.byteLength;
    }

    return {
      pts,
      dts,
      keyframe,
      data,
    };
  }


  private parseNals(pes: PES): NAL[] {
    const packetNalus: NAL[] = [];
    const buffer = pes.data;

    let pos = 0;
    let nalStart: number | undefined;
    while (true) {
      // Find the next start code 0x000001 or 0x00000001
      let afterStartCode: number | undefined;
      while (pos < buffer.byteLength - 3) {
        if (buffer[pos] === 0 && buffer[pos + 1] === 0) {
          if (buffer[pos + 2] === 1) {
            afterStartCode = pos + 3;
            break;
          } else if (buffer[pos + 2] === 0 && buffer[pos + 3] === 1) {
            afterStartCode = pos + 4;
            break;
          }
        }
        pos++;
      }

      let endOfNal: number;
      if (afterStartCode === undefined) {
        endOfNal = buffer.byteLength;
        pos = buffer.byteLength;
      } else {
        endOfNal = afterStartCode - 3;
        pos = afterStartCode;
        if (buffer[endOfNal - 1] === 0) {
          endOfNal--;
        }
      }

      if (nalStart !== undefined) {
        const data = buffer.subarray(nalStart, endOfNal);
        packetNalus.push({
          pts: pes.pts,
          dts: pes.dts,
          nalUnitCode: data[0]! & 0x1f,
          data,
        });
      }
      if (afterStartCode === undefined) {
        break;
      }
      pos = afterStartCode;
      nalStart = afterStartCode;
    }

    return packetNalus;
  }

  /**
   * Now that we have all the PES packets for the video stream,
   * We can use these packets to find the NAL units and group
   * them into frames.
   */
  private processVideoStream(pesBuffer: PES[]): EncodedVideoData {
    let lastFrameInfo: H264FrameInfo | undefined;
    let width: number | undefined;
    let height: number | undefined;
    let codec = 'avc1.42c020';
    let progressive = true;
    const frames: EncodedVideoFrame[] = [];

    const captionDataTriplets: CaptionDataTriplet[] = [];

    const nalus: NAL[] = [];
    let waitingForFirstAud = true;

    // This value is used to keep track of whether we have a full frame or only half a frame
    let hasSingleField = false;

    const pts: number[] = [];
    for (const pes of pesBuffer) {
      const packetNalus = this.parseNals(pes);
      for (const nalu of packetNalus) {
        if (lastFrameInfo && (!lastFrameInfo.frame_mbs_only_flag && !lastFrameInfo.mb_adaptive_frame_field_flag)
            && (nalu.nalUnitCode === slice_layer_without_partitioning_rbsp_non_idr || nalu.nalUnitCode === slice_layer_without_partitioning_rbsp_idr)
        ) {
          // We are in the interlaced mode where each access unit may be either a frame or field,
          // and we have a NAL unit with a slice header to inspect to figure out which one it is
          const rbsp = decodeRBSP(nalu.data.subarray(1));
          const sliceHeaderInfo = inspectSliceHeader(rbsp, lastFrameInfo);
          if (sliceHeaderInfo.first_mb_in_slice === 0) {
            // The field_pic_flag value should be the same for all slices of a given field/frame,
            // so we only process it for the first macroblock
            if (sliceHeaderInfo.field_pic_flag) {
              hasSingleField = !hasSingleField;
            } else {
              hasSingleField = false;
            }
          }
        }

        if (waitingForFirstAud && nalu.nalUnitCode !== access_unit_delimiter_rbsp) {
          // An h264 frame must start with an access unit delimiter NAL, so let's
          // ignore all other frames when we are at the start of the stream
          continue;
        }

        if (this.processClosedCaptionData !== undefined && nalu.nalUnitCode === sei_rbsp) {
          const rbsp = decodeRBSP(nalu.data.subarray(1));
          parseSeiForClosedCaptions(rbsp, captionDataTriplets);
          continue;
        }

        if (nalu.nalUnitCode === access_unit_delimiter_rbsp) {
          waitingForFirstAud = false;
          if (nalus.length > 0 && !hasSingleField) {
            if (this.processClosedCaptionData) {
              this.processClosedCaptionData(nalus[0]!.pts, captionDataTriplets.splice(0));
            }

            // If we had NALs, we can now group them into a frame
            const frame = this.makeEncodedVideoFrame(nalus.splice(0));
            frames.push(frame);
            pts.push(frame.pts);
          }
        } else if (nalu.nalUnitCode === seq_parameter_set_rbsp) {
          // If we don't know yet about the width and height, let's decode
          // the NAL that contains them. But first, we need to decode the
          // NAL data since it is escaped
          const rbsp = decodeRBSP(nalu.data.subarray(1));
          const info = parseSequenceparameterSet(rbsp);
          if (lastFrameInfo === undefined) {
            codec = getCodecString(info);
            width = info.width;
            height = info.height;
          }
          progressive = info.frame_mbs_only_flag;
          if (progressive) {
            hasSingleField = false;
          }
          lastFrameInfo = info;
        }
        nalus.push(nalu);
      }
    }

    if (nalus.length > 0) {
      // Let's build the last frame
      if (this.processClosedCaptionData) {
        this.processClosedCaptionData(nalus[0]!.pts, captionDataTriplets.splice(0));
      }
      const frame = this.makeEncodedVideoFrame(nalus.splice(0));
      frames.push(frame);
      pts.push(frame.pts);
    }

    pts.sort();
    return {
      codec,
      description: undefined,
      timescale: 90000,
      epsilon: { numerator: 2, denominator: 90000 },
      colorSpace: null,
      width,
      height,
      pixelAspectRatio: lastFrameInfo?.sampleAspectRatio,
      progressive,
      frameDuration: (pts.length > 1) ? pts[1]! - pts[0]! : Infinity,
      frames,
      // mpegts's 90000 ticks per second cannot represent accurately framerates like 24000/1001
      exactFrameDuration: false,
    };
  }


  private processAudioStream(pesBuffer: PES[]): EncodedAudioData {
    let numberOfChannels = 2;
    let sampleRateInHz = 48000;
    const frames: EncodedAudioFrame[] = [];
    let codec = 'mp4a.40.2';
    let codecSpecificData: AudioCodecSpecificData | null = null;

    for (const pes of pesBuffer) {
      const adtsFrame = parseAdtsFrame(pes.pts, pes.dts, pes.data);
      invariant(adtsFrame !== undefined, () => `Could not parse ADTS frame at PTS ${pes.pts}`);
      codecSpecificData= adtsFrame.raw;
      if (frames.length === 0) {
        numberOfChannels = adtsFrame.numberOfChannels;
        sampleRateInHz = adtsFrame.sampleRateInHz;
        codec = `mp4a.40.${adtsFrame.raw.audioObjectType}`;
      }
      for (const frame of adtsFrame.frames) {
        if (!this.trimAudioFramesBeforeVideo || this.earliestVideoDts === undefined || frame.dts >= this.earliestVideoDts) {
          frames.push(frame);
        }
      }
    }

    return {
      codec,
      timescale: 90000,
      epsilon: { numerator: 2, denominator: 90000 },
      numberOfChannels,
      sampleRateInHz,
      frames,
      codecSpecificData,
    };
  }


  public parse(): EncodedData {
    this.readMpegTsPackets();
    const pmtId = this.findPmtId();
    invariant(pmtId !== null, 'Could not find PMT ID');

    const pmt = this.readProgramMapTable(pmtId);
    invariant(pmt !== undefined, 'Could not find PMT');

    const streamMap = this.readPacketizedElementaryStreams(pmt);

    const encodedVideoData = pmt.videoPid === undefined ? undefined : this.processVideoStream(streamMap[pmt.videoPid]!);
    const encodedAudioData: EncodedAudioData[] = [];

    const sortedAudioPids = pmt.audioPids.sort();
    for (const audioPid of sortedAudioPids) {
      encodedAudioData.push(this.processAudioStream(streamMap[audioPid]!));
    }

    return {
      encodedVideoData,
      encodedAudioData,
    };
  }

}
